import { createSlice, current } from "@reduxjs/toolkit";
import { uploadFile } from "libs/api/file";

import {
  getMitraThunk,
  createMitraThunk,
  updateMitraThunk,
  deleteMitraThunk,
  bulkCreateMitraThunk,
} from "state/action/mitra";

const getDetailsStructure = (itemType) => {
  switch (itemType) {
    case "Additional Service":
      return {
        service: "",
        item: "",
      };
    case "Intangible Rights":
      return {
        asset_name: "",
        station_code: "",
        contract_period_start: "",
        contract_period_end: "",
      };
    case "Unit":
      return {
        property: "",
        building: "",
        floor: "",
        unit: "",
        fitting_in: "",
        fitting_out: "",
        initial_handover: "",
        contract_start: "",
        contract_end: "",
      };
    case "One Time Fee":
      return {
        scope: "",
      };
    case "Casual Lease":
      return {
        location: "",
        event_dates: "",
      };
    default:
      // Kembali ke objek kosong jika tipe tidak dikenali
      return {};
  }
};

// Initial state
const initialState = {
  // Get Mitra inisial state
  getMitra: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Create Mitra inisial state
  createMitra: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Update Mitra inisial state
  updateMitra: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete Mitra inisial state
  deleteMitra: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Mitra object value
  mitra_object_value: "",
  createMitraPerusahaanInputBody: {
    nama_badan_usaha: "",
    // jenis_mitra: "",
    kategori_industri: [],
    brand: "",
    nomor_induk_berusaha: "",
    alamat_kantor: "",
    email_kantor: "",
    nomor_handphone_kantor: "",
    badan_usaha: "",
    status: "active",
    direksi: [],
    legal_document: [
      {
        name: "",
        type: "",
        nomor: "",
        tanggal: "",
        file: "",
        url: "",
      },
    ],
    contact_person: [],
    tax_information: {
      npwp: "",
      npwp_image: {
        url: "",
        file_name: "",
      },
      nama_wajib_pajak: "",
      kantor_pajak: "",
      alamat_wajib_pajak: "",
    },
  },
  createMitraInputBody: {
    nama_mitra: "",
    tipe_mitra: "",
    jenis_mitra: "",
    alamat_perusahaan: "",
    signing_official: {
      name: "",
      title: "",
    },
    brand: "",
    kontak: {
      nama_pic: "",
      jabatan_pic: "",
      email: "",
      telepon: "",
      alamat: "",
      personal_id: "",
    },
    informasi_pajak: {
      nama_wajib_pajak: "",
      npwp: "",
      alamat_wajib_pajak: "",
    },
    legal_document: [
      {
        url: "",
        file: "",
      },
    ],
  },

  selectedMitra: {
    nama_mitra: "",
    tipe_mitra: "",
    jenis_mitra: "",
    alamat_perusahaan: "",
    signing_official: {
      name: "",
      title: "",
    },
    brand: "",
    kontak: {
      nama_pic: "",
      jabatan_pic: "",
      email: "",
      telepon: "",
      alamat: "",
      personal_id: "",
    },
    informasi_pajak: {
      nama_wajib_pajak: "",
      npwp: "",
      alamat_wajib_pajak: "",
    },
    legal_document: [
      {
        url: "",
        file: "",
      },
    ],
  },
  editMitraInputBody: {
    status: "",
  },
  // MitraFormDialog UI state (validation errors, "add new" draft rows, save-loading)
  formUi: {
    errors: {},
    loading: false,
    newContactPerson: {
      nama: "",
      jabatan: "",
      nomor_handphone: "",
      email: "",
    },
    newDireksi: {
      nama: "",
      jabatan: "",
    },
    newLegalDoc: {
      name: "",
      type: "",
      nomor: "",
      tanggal: null,
      file: null,
    },
  },
};

export const mitraSlice = createSlice({
  name: "mitra",
  initialState,
  reducers: {
    // 1. Root Level Fields
    setMitraField: (state, action) => {
      const { field, value } = action.payload;
      state.createMitraPerusahaanInputBody[field] = value;
    },

    // 2. Nested Object Fields (alamat_kantor, tax_information)
    setNestedField: (state, action) => {
      const { parent, field, value } = action.payload;

      state.createMitraPerusahaanInputBody[parent][field] = value;
    },

    // 3. Array Fields (direksi, contact_person) - Update Item
    updateArrayItem: (state, action) => {
      const { arrayName, index, field, value } = action.payload;
      state.createMitraPerusahaanInputBody[arrayName][index][field] = value;
    },
    // Add Item to Array
    addArrayItem: (state, action) => {
      const { arrayName, newItem } = action.payload;

      state.createMitraPerusahaanInputBody[arrayName].push(newItem);
    },
    // Remove Item from Array
    removeArrayItem: (state, action) => {
      const { arrayName, index } = action.payload;
      state.createMitraPerusahaanInputBody[arrayName].splice(index, 1);
    },

    // 4. File Handling
    setTaxImage: (state, action) => {
      // action.payload = { file: FileObject, url: BlobUrl }
      state.createMitraPerusahaanInputBody.tax_information.npwp_image =
        action.payload;
    },
    addLegalDoc: (state, action) => {
      state.createMitraPerusahaanInputBody.legal_document.push(
        ...action.payload,
      );
    },
    removeLegalDoc: (state, action) => {
      state.createMitraPerusahaanInputBody.legal_document.splice(
        action.payload,
        1,
      );
    },

    resetMitraForm: (state) => {
      state.createMitraPerusahaanInputBody =
        initialState.createMitraPerusahaanInputBody;
    },
    addLegalDocuments: async (state, action) => {
      // `action.payload` adalah FileList, yang tidak seperti array biasa.
      // Kita ubah menjadi array agar bisa di-map.
      // const filesArray = Array.from(action.payload);

      // const formattedFiles = filesArray.map((file) => ({
      //   // Membuat URL sementara untuk preview jika dibutuhkan (misal: untuk gambar)
      //   url: file.url,
      //   file: file.file_name, // Simpan objek File asli untuk di-upload nanti
      //   name: file.original_name,
      // }));

      // Tambahkan file-file yang baru diformat ke dalam array yang sudah ada
      state.createMitraPerusahaanInputBody.legal_document.push({
        // Membuat URL sementara untuk preview jika dibutuhkan (misal: untuk gambar)
        url: action.payload.url,
        file: action.payload.file_name, // Simpan objek File asli untuk di-upload nanti
        name: action.payload.original_name,
      });
    },

    /**
     * Action untuk menghapus satu file berdasarkan namanya.
     * Payload yang diharapkan adalah string nama file yang akan dihapus.
     */
    removeLegalDocument: (state, action) => {
      const fileNameToRemove = action.payload;
      state.createMitraPerusahaanInputBody.legal_document =
        state.createMitraPerusahaanInputBody.legal_document.filter(
          (doc) => doc.name !== fileNameToRemove,
        );
    },

    /**
     * Action untuk membersihkan semua file yang sudah dipilih.
     */
    clearLegalDocuments: (state) => {
      state.createMitraPerusahaanInputBody.legal_document = [];
    },
    // Reducer untuk mengubah field sederhana (misal: nama_badan_usaha)
    // setMitraField: (state, action) => {
    //   const { path, value } = action.payload;
    //   const keys = path.split(".");
    //   let current = state.createMitraPerusahaanInputBody;
    //   for (let i = 0; i < keys.length - 1; i++) {
    //     current = current[keys[i]];
    //   }
    //   current[keys[keys.length - 1]] = value;
    // },
    // Reducer untuk menambah item ke array
    addMitraListItem: (state, action) => {
      const { listName, newItem } = action.payload;
      state.createMitraPerusahaanInputBody[listName].push(newItem);
    },
    // Reducer untuk menghapus item dari array
    removeMitraListItem: (state, action) => {
      const { listName, index } = action.payload;
      state.createMitraPerusahaanInputBody[listName].splice(index, 1);
    },
    // Reducer untuk mengupdate item di dalam array
    updateMitraListItem: (state, action) => {
      const { listName, index, field, value } = action.payload;

      if (field) {
        state.createMitraPerusahaanInputBody[listName][index][field] = value;
      } else {
        state.createMitraPerusahaanInputBody[listName][index] = value;
      }
    },

    // Partial update of MitraFormDialog UI state, e.g. dispatch(setMitraFormUi({ loading: true }))
    setMitraFormUi: (state, action) => {
      Object.assign(state.formUi, action.payload);
    },
    resetMitraFormUi: (state) => {
      state.formUi = initialState.formUi;
    },

    setGetMitraObjectValue: (state, action) => {
      return {
        ...state,
        Mitra_object_value: action.payload,
      };
    },
    updateQuotationSummary: (state, action) => {
      const { subtotal, ppnAmount } = action.payload;
      const summary = state.createMitraInputBody.summary;

      summary.subtotal = subtotal;
      summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      summary.grand_total = subtotal + ppnAmount;
    },

    // INI BAGIAN PENTINGNYA: Action untuk mengubah tipe item
    updateItemType: (state, action) => {
      // Payload harus berisi index item dan tipe barunya
      const { itemIndex, newType } = action.payload;

      // Ambil item yang akan diubah
      const itemToUpdate = state.createMitraInputBody.services[itemIndex];

      if (itemToUpdate) {
        // Update service_type
        itemToUpdate.service_type = newType;
        // Ganti keseluruhan objek 'details' dengan struktur baru
        itemToUpdate.details = getDetailsStructure(newType);
      }
    },

    updateItem: (state, action) => {
      const { index, field, value } = action.payload;
      const items = state.createMitraInputBody.services;

      // 1. Update nilai field pada item yang spesifik
      if (items[index]) {
        items[index][field] = value;
      }

      // 2. Hitung ulang total_price untuk item tersebut
      // Selalu pastikan quantity dan unit_price adalah angka
      const quantity = Number(items[index].quantity) || 0;
      const unitPrice = Number(items[index].unit_price) || 0;
      items[index].total_price = quantity * unitPrice;

      // 3. Hitung ulang summary (subtotal dan PPN) dari awal
      const subtotal = items.reduce(
        (sum, item) => sum + (Number(item.total_price) || 0),
        0,
      );
      const ppnAmount = subtotal * 0.11;

      const summary = state.createQuotationInputBody.summary;

      summary.subtotal = subtotal;
      summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      summary.grand_total = subtotal + ppnAmount;
    },

    // Action untuk mengubah nilai di dalam 'details'
    updateItemDetails: (state, action) => {
      const { itemIndex, field, value } = action.payload;

      const itemToUpdate = state.createMitraInputBody.services[itemIndex];

      if (itemToUpdate && itemToUpdate.details) {
        itemToUpdate.details[field] = value;
      }
    },
    setSelectedMitra: (state, action) => {
      return {
        ...state,
        selectedMitra: action.payload,
      };
    },
    setCreateMitraInputBody: (state, action) => {
      return {
        ...state,
        createMitraInputBody: action.payload,
      };
    },
    setCreateMitraPerusahaanInputBody: (state, action) => {
      return {
        ...state,
        createMitraPerusahaanInputBody: action.payload,
      };
    },
    setEditMitraInputBody: (state, action) => {
      return {
        ...state,
        editMitraInputBody: action.payload,
      };
    },
    setClearCreateMitraPerusahaanInputBody: (state, _) => {
      return {
        ...state,
        createMitraPerusahaanInputBody: {
          nama_badan_usaha: "",
          // jenis_mitra: "",
          kategori_industri: [],
          brand: "",
          nomor_induk_berusaha: "",
          alamat_kantor: {
            alamat1: "",
            alamat2: "",
            kelurahan: "",
            kecamatan: "",
            kota: "",
            provinsi: "",
            kode_pos: "",
            email: "",
            nomor_handphone: "",
          },
          badan_usaha: "",
          direksi: [
            {
              nama: "",
              jabatan: "",
            },
          ],
          legal_document: [],
          contact_person: [
            {
              nama: "",
              jabatan: "",
              bagian: "",
              nomor_handphone: "",
              email: "",
              status: "",
            },
          ],
          tax_information: {
            npwp: "",
            npwp_image: {
              url: "",
              file: "",
            },
            nama_wajib_pajak: "",
            kantor_pajak: "",
            alamat_wajib_pajak: "",
          },
        },
      };
    },
    // note: clear state
    setClearGetMitraObjectValue: (state, _) => {
      return {
        ...state,
        mitra_object_value: "",
      };
    },
    setClearGetMitra: (state, _) => {
      return {
        ...state,
        getMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearCreateMitra: (state, _) => {
      return {
        ...state,
        createMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearUpdateMitra: (state, _) => {
      return {
        ...state,
        updateMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearDeleteMitra: (state, _) => {
      return {
        ...state,
        deleteMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearAllMitraState: (state, _) => {
      return {
        ...state,
        // Get Mitra inisial state
        getMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Create Mitra inisial state
        createMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Update Mitra inisial state
        updateMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Delete Mitra inisial state
        deleteMitra: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Mitra object value
        mitra_object_value: "",
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get Mitra reducer
      .addCase(getMitraThunk.pending, (state, _) => {
        return {
          ...state,
          getMitra: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getMitraThunk.fulfilled, (state, action) => {
        return {
          ...state,
          getMitra: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getMitraThunk.rejected, (state, action) => {
        return {
          ...state,
          getMitra: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // Create Mitra reducer
      .addCase(createMitraThunk.pending, (state, _) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createMitraThunk.fulfilled, (state, action) => {
        return {
          ...state,
          register: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createMitraThunk.rejected, (state, action) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // update Mitra reducer
      .addCase(updateMitraThunk.pending, (state, _) => {
        return {
          ...state,
          updateMitra: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateMitraThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updateMitra: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateMitraThunk.rejected, (state, action) => {
        return {
          ...state,
          updateMitra: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      });
  },
});

export const mitraAction = {
  ...mitraSlice.actions,
  getMitraThunk,
  createMitraThunk,
  updateMitraThunk,
  deleteMitraThunk,
  bulkCreateMitraThunk,
};

export function selectMitraObjectValue(state) {
  return state?.mitra?.mitra_object_value;
}

export function selectMitra(state) {
  return state?.mitra;
}

export function selectMitraFormUi(state) {
  return state?.mitra?.formUi;
}

export default mitraSlice.reducer;
